import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';

/**
 * CurrencyInput - Input field for monetary values
 * 
 * CRITICAL FIX: This component now properly handles user typing without
 * interfering with keystrokes. The value is only normalized on blur.
 * 
 * Features:
 * - No spinner arrows (type="text" with inputMode="decimal")
 * - Accepts comma as decimal separator (PT-PT)
 * - Normalizes to 2 decimal places ONLY on blur
 * - Prevents negative values when configured
 * - ArrowUp/ArrowDown do NOT change the value
 * - Keys 0-9 work normally without increment/decrement
 */
export const CurrencyInput = ({
  value,
  onChange,
  allowNegative = false,
  placeholder = '0,00',
  className = '',
  disabled = false,
  max,
  'data-testid': testId,
  ...props
}) => {
  // Local display value - only sync from prop on mount or when not focused
  const [displayValue, setDisplayValue] = useState(() => (
    value === null || value === undefined || isNaN(value) ? '' : (Math.round(value * 100) / 100).toFixed(2).replace('.', ',')
  ));
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const lastExternalValue = useRef(value);
  
  // Format number to display string with comma
  const formatDisplay = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '';
    // Round to 2 decimal places to avoid floating point issues
    const rounded = Math.round(num * 100) / 100;
    return rounded.toFixed(2).replace('.', ',');
  };
  
  // Parse display string to number
  const parseDisplay = (str) => {
    if (!str || str.trim() === '') return null;
    // Replace comma with dot and remove non-numeric chars except dot and minus
    const normalized = str.replace(',', '.').replace(/[^\d.-]/g, '');
    const num = parseFloat(normalized);
    if (isNaN(num)) return null;
    // Round to 2 decimal places
    return Math.round(num * 100) / 100;
  };
  
  // Initialize display value from prop ONLY when not focused
  useEffect(() => {
    // Only sync from external value if:
    // 1. We're not focused (user is not typing)
    // 2. The external value actually changed
    if (!isFocused && value !== lastExternalValue.current) {
      lastExternalValue.current = value;
      if (value !== null && value !== undefined) {
        setDisplayValue(formatDisplay(value));
      } else {
        setDisplayValue('');
      }
    }
  }, [value, isFocused]);
  
  // Handle input change - just store the text, don't notify parent yet
  const handleChange = (e) => {
    const input = e.target.value;
    
    // Allow empty input
    if (!input) {
      setDisplayValue('');
      return;
    }
    
    // Accept digits, comma, dot, and minus (if allowed)
    // Allow the user to type freely without validation blocking
    const validChars = allowNegative ? /^-?[\d,.\s]*$/ : /^[\d,.\s]*$/;
    if (!validChars.test(input)) return;
    
    // Just update the display - don't notify parent during typing
    setDisplayValue(input);
  };
  
  // Handle focus - track that user is editing
  const handleFocus = () => {
    setIsFocused(true);
  };
  
  // Handle blur - normalize and notify parent
  const handleBlur = () => {
    setIsFocused(false);
    
    // Parse the display value
    const parsed = parseDisplay(displayValue);
    
    if (parsed !== null) {
      let finalValue = parsed;
      
      // Check negative
      if (!allowNegative && finalValue < 0) {
        finalValue = 0;
      }
      
      // Update display with formatted value
      setDisplayValue(formatDisplay(finalValue));
      lastExternalValue.current = finalValue;
      
      // Notify parent
      onChange(finalValue);
    } else if (displayValue.trim() === '') {
      setDisplayValue('');
      lastExternalValue.current = null;
      onChange(null);
    } else {
      // Invalid input - reset to last valid value
      const resetValue = value !== null ? formatDisplay(value) : '';
      setDisplayValue(resetValue);
    }
  };
  
  // Prevent ArrowUp/ArrowDown from doing anything
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      return;
    }
  };
  
  return (
    <Input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      className={`text-right font-mono ${className}`}
      data-testid={testId}
      style={{
        // Ensure no spinner arrows in any browser
        MozAppearance: 'textfield',
        WebkitAppearance: 'none',
      }}
      {...props}
    />
  );
};

/**
 * Format currency value safely, avoiding floating point display issues
 */
export const formatCurrencyValue = (value) => {
  if (value === null || value === undefined) return '—';
  // Round to 2 decimal places
  const rounded = Math.round(value * 100) / 100;
  return new Intl.NumberFormat('pt-PT', { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(rounded);
};

/**
 * Round monetary value to 2 decimal places (for calculations)
 * Uses integer math to avoid floating point errors
 */
export const roundMoney = (value) => {
  if (value === null || value === undefined || isNaN(value)) return 0;
  return Math.round(value * 100) / 100;
};

export default CurrencyInput;
